import {
  offsetToPageIndex,
  pageIndexToOffset,
} from "@/components/data-table";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

const PAGE_SIZE_OPTIONS = [10, 20, 30, 50];

type PageItem = number | "ellipsis-left" | "ellipsis-right";

function getPageItems(current: number, total: number): PageItem[] {
  if (total <= 1) return [1];
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const items: PageItem[] = [1];
  const left = Math.max(2, current - 1);
  const right = Math.min(total - 1, current + 1);
  if (left > 2) items.push("ellipsis-left");
  for (let page = left; page <= right; page++) items.push(page);
  if (right < total - 1) items.push("ellipsis-right");
  items.push(total);
  return items;
}

type OrdersListPaginationProps = {
  offset: number;
  limit: number;
  totalCount: number;
  isLoading?: boolean;
  onChange: (offset: number, limit: number) => void;
};

export function OrdersListPagination({
  offset,
  limit,
  totalCount,
  isLoading,
  onChange,
}: OrdersListPaginationProps) {
  const pageCount = limit > 0 ? Math.ceil(totalCount / limit) : 0;
  const currentPage = offsetToPageIndex(offset, limit) + 1;
  const isFirstPage = currentPage <= 1;
  const isLastPage = pageCount === 0 || currentPage >= pageCount;
  const pageItems = getPageItems(currentPage, pageCount);

  function goToPage(page: number) {
    const clamped = Math.max(1, Math.min(page, pageCount));
    onChange(pageIndexToOffset(clamped - 1, limit), limit);
  }

  if (totalCount === 0) return null;

  return (
    <div className="flex flex-col-reverse items-center gap-3 px-1 sm:flex-row sm:justify-between">
      <div className="flex items-center gap-4">
        <p className="whitespace-nowrap text-muted-foreground text-sm">
          {`${offset + 1}–${Math.min(offset + limit, totalCount)} ຈາກ ${totalCount}`}
        </p>
        <div className="flex items-center gap-2">
          <span className="hidden whitespace-nowrap text-muted-foreground text-sm sm:inline">
            ຕໍ່ໜ້າ
          </span>
          <Select
            value={String(limit)}
            onValueChange={(value) => onChange(0, Number(value))}
            disabled={isLoading}
          >
            <SelectTrigger size="sm" className="w-[78px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Pagination className="mx-0 w-auto justify-end">
        <PaginationContent>
          <PaginationItem>
            <PaginationPrevious
              aria-disabled={isFirstPage}
              className={cn(
                "cursor-pointer select-none",
                isFirstPage && "pointer-events-none opacity-50",
              )}
              onClick={(event) => {
                event.preventDefault();
                if (!isFirstPage) goToPage(currentPage - 1);
              }}
            />
          </PaginationItem>

          {pageItems.map((item) =>
            typeof item === "number" ? (
              <PaginationItem key={item}>
                <PaginationLink
                  href="#"
                  isActive={item === currentPage}
                  className="cursor-pointer select-none"
                  onClick={(event) => {
                    event.preventDefault();
                    goToPage(item);
                  }}
                >
                  {item}
                </PaginationLink>
              </PaginationItem>
            ) : (
              <PaginationItem key={item}>
                <PaginationEllipsis />
              </PaginationItem>
            ),
          )}

          <PaginationItem>
            <PaginationNext
              aria-disabled={isLastPage}
              className={cn(
                "cursor-pointer select-none",
                isLastPage && "pointer-events-none opacity-50",
              )}
              onClick={(event) => {
                event.preventDefault();
                if (!isLastPage) goToPage(currentPage + 1);
              }}
            />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    </div>
  );
}
